import React, { useMemo, useState } from 'react';
import { FiX, FiPrinter } from 'react-icons/fi';
import SafeIcon from '../../common/SafeIcon';
import { buildAmortizationSchedule } from '../../config/amortization';

const money = (v) =>
  Number(v || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const dateStr = (d) =>
  d instanceof Date ? `${d.getMonth() + 1}/${d.getDate()}/${d.getFullYear()}` : '';

// DeskManager-style amortization table for the current deal model.
const AmortizationModal = ({ model, onClose }) => {
  const [includeCashDown, setIncludeCashDown] = useState(false);
  const [includeDeferredDowns, setIncludeDeferredDowns] = useState(false);

  const schedule = useMemo(
    () => buildAmortizationSchedule(model, { includeCashDown, includeDeferredDowns }),
    [model, includeCashDown, includeDeferredDowns]
  );

  const vehicleTitle = [model.vehicle.year, model.vehicle.make, model.vehicle.model]
    .filter(Boolean)
    .join(' ');

  const printSchedule = () => {
    const rowsHtml = schedule.rows
      .map(
        (row) => `<tr>
          <td>${row.downPayment ? row.label : row.n}</td>
          <td>${dateStr(row.dueDate)}</td>
          <td class="r">${row.downPayment ? '' : row.days}</td>
          <td class="r">${money(row.payment)}</td>
          <td class="r">${row.downPayment ? '' : money(row.principal)}</td>
          <td class="r">${row.downPayment ? '' : money(row.interest)}</td>
          <td class="r">${row.downPayment ? '' : money(row.totPrincipal)}</td>
          <td class="r">${row.downPayment ? '' : money(row.totInterest)}</td>
          <td class="r">${money(row.totPayments)}</td>
          <td class="r">${row.downPayment ? '' : money(row.balance)}</td>
        </tr>`
      )
      .join('');
    const win = window.open('', '_blank');
    if (!win) return;
    win.document.write(`<!doctype html><html><head><title>Amortization — ${vehicleTitle}</title>
      <style>
        body { font-family: Arial, sans-serif; font-size: 12px; margin: 24px; }
        h1 { font-size: 16px; } h2 { font-size: 13px; font-weight: normal; }
        table { border-collapse: collapse; width: 100%; margin-top: 12px; }
        th, td { border: 1px solid #ccc; padding: 4px 6px; }
        th { background: #f0f0f0; text-align: right; }
        th:first-child, th:nth-child(2) { text-align: left; }
        td.r { text-align: right; }
      </style></head><body>
      <h1>Amortization Schedule — ${vehicleTitle}</h1>
      <h2>Buyer: ${model.buyer.firstName} ${model.buyer.lastName} ·
        Amount financed $${money(schedule.amountFinanced)} · APR ${schedule.apr}% ·
        ${schedule.term} ${schedule.frequency.toLowerCase()} payments</h2>
      <table><thead><tr>
        <th>No.</th><th>Due Date</th><th>Days</th><th>Payment</th><th>Principal</th>
        <th>Interest</th><th>Tot. Prin.</th><th>Tot. Int.</th><th>Tot. Pmts.</th><th>Balance</th>
      </tr></thead><tbody>${rowsHtml}</tbody></table>
      <script>window.print();</script></body></html>`);
    win.document.close();
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-xl max-w-4xl w-full max-h-[90vh] flex flex-col">
        <div className="p-4 border-b flex justify-between items-center">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">Amortization — {vehicleTitle || 'Deal'}</h2>
            <p className="text-sm text-gray-500">
              Amount financed <strong>${money(schedule.amountFinanced)}</strong> ·
              APR <strong>{schedule.apr}%</strong> ·
              Term <strong>{schedule.term}</strong> ({schedule.frequency.toLowerCase()})
            </p>
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={printSchedule}
              className="px-3 py-2 bg-primary-600 text-white rounded-lg text-sm font-medium hover:bg-primary-700 flex items-center gap-1.5"
            >
              <SafeIcon icon={FiPrinter} className="w-4 h-4" /> Print
            </button>
            <button
              type="button"
              onClick={onClose}
              className="p-2 text-gray-500 hover:text-gray-700"
              aria-label="Close"
            >
              <SafeIcon icon={FiX} className="w-5 h-5" />
            </button>
          </div>
        </div>

        <div className="px-4 py-2 border-b flex flex-wrap gap-4 text-sm text-gray-700">
          <label className="flex items-center gap-2">
            <input
              type="checkbox"
              checked={includeCashDown}
              onChange={(e) => setIncludeCashDown(e.target.checked)}
            />
            Include cash down
          </label>
          <label className="flex items-center gap-2">
            <input
              type="checkbox"
              checked={includeDeferredDowns}
              onChange={(e) => setIncludeDeferredDowns(e.target.checked)}
            />
            Include deferred down payments
          </label>
        </div>

        <div className="overflow-auto p-4">
          {schedule.rows.length === 0 ? (
            <p className="text-sm text-gray-500">
              No payment schedule — set the term, APR, and first payment date on this deal first.
            </p>
          ) : (
            <table className="w-full text-sm">
              <thead className="sticky top-0 bg-gray-100">
                <tr className="text-gray-700">
                  <th className="px-2 py-1.5 text-left">No.</th>
                  <th className="px-2 py-1.5 text-left">Due Date</th>
                  <th className="px-2 py-1.5 text-right">Days</th>
                  <th className="px-2 py-1.5 text-right">Payment</th>
                  <th className="px-2 py-1.5 text-right">Principal</th>
                  <th className="px-2 py-1.5 text-right">Interest</th>
                  <th className="px-2 py-1.5 text-right">Tot. Prin.</th>
                  <th className="px-2 py-1.5 text-right">Tot. Int.</th>
                  <th className="px-2 py-1.5 text-right">Tot. Pmts.</th>
                  <th className="px-2 py-1.5 text-right">Balance</th>
                </tr>
              </thead>
              <tbody>
                {schedule.rows.map((row, i) => (
                  <tr key={i} className={`border-b ${row.downPayment ? 'bg-amber-50' : i % 2 ? 'bg-gray-50' : ''}`}>
                    <td className="px-2 py-1">{row.downPayment ? row.label : row.n}</td>
                    <td className="px-2 py-1">{dateStr(row.dueDate)}</td>
                    <td className="px-2 py-1 text-right">{row.downPayment ? '' : row.days}</td>
                    <td className="px-2 py-1 text-right">{money(row.payment)}</td>
                    <td className="px-2 py-1 text-right">{row.downPayment ? '' : money(row.principal)}</td>
                    <td className="px-2 py-1 text-right">{row.downPayment ? '' : money(row.interest)}</td>
                    <td className="px-2 py-1 text-right">{row.downPayment ? '' : money(row.totPrincipal)}</td>
                    <td className="px-2 py-1 text-right">{row.downPayment ? '' : money(row.totInterest)}</td>
                    <td className="px-2 py-1 text-right">{money(row.totPayments)}</td>
                    <td className="px-2 py-1 text-right">{row.downPayment ? '' : money(row.balance)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
};

export default AmortizationModal;
